import { Partner, partners } from 'noddi-types';
import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';

export interface ThemeStoreProps {
  initialTheme: Partner;
}

interface ThemeState {
  currentTheme: Partner;
  setTheme: (theme: Partner) => void;
}

const applyThemeClasses = (theme: Partner) => {
  const root = document.documentElement;

  // Remove all theme classes first
  Object.values(partners).forEach((className) => {
    if (className) {
      root.classList.remove(className);
    }
  });

  // Add the new theme class if it's not default
  const newThemeClass = partners[theme];
  if (newThemeClass) {
    root.classList.add(newThemeClass);
  }
};

export type ThemeStore = ReturnType<typeof createThemeStore>;

export const createThemeStore = (props: ThemeStoreProps) => {
  return createStore<ThemeState>()((set) => ({
    currentTheme: props.initialTheme,
    setTheme: (theme) => {
      set({ currentTheme: theme });
      applyThemeClasses(theme);
    }
  }));
};

export const ThemeContext = createContext<ThemeStore | null>(null);

function useThemeContext<T>(selector: (state: ThemeState) => T): T {
  const store = useContext(ThemeContext);
  if (!store) {
    throw new Error('Missing ThemeContext.Provider in the tree');
  }
  return useStore(store, selector);
}

export const useCurrentTheme = () => useThemeContext((state) => state.currentTheme);
export const useSetTheme = () => useThemeContext((state) => state.setTheme);
